import type { Column } from '../../claim-history/simple-table'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useReducer } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../lib/http'
import * as Table from '../../claim-history/simple-table'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import styles from '../index.module.css'

interface Entry extends Record<PropertyKey, unknown> {
  id: string
  hash: string
  client_name?: string | null
  patient_id?: string | null
  first_name?: string | null
  last_name?: string | null
  birth_date?: string | null
  ssn?: string | null
  client?: string | null
  status?: SelfEnrollmentStatus | null
}

const enum SelfEnrollmentStatus {
  Started = 'started',
  Finished = 'finished',
  None = 'none'
}

let selfEnrollmentStatusMapper: Record<SelfEnrollmentStatus, string> = {
  [SelfEnrollmentStatus.Started]: 'Started',
  [SelfEnrollmentStatus.Finished]: 'Finished',
  [SelfEnrollmentStatus.None]: 'None'
}

interface Client {
  key: string
  display: string
  group_id: string
  entity: string
}

type QueryKey = [
  'self-enrollment',
  SearchParams
]

interface QueryData {
  data: Entry[]
  total: number
  count: number
}

interface State {
  searchParams: SearchParams
}

type Action =
  | { type: 'query/set', payload: string }
  | { type: 'filters/set', payload: SearchParams['filters'] }
  | { type: 'filters/merge', payload: Partial<SearchParams['filters']> }
  | { type: 'filters/clear' }
  | { type: 'sort/set', payload: Sort }
  | { type: 'pagination/page/set', payload: number }
  | { type: 'pagination/page-size/set', payload: number }

interface SearchParams {
  query: string

  filters: {
    status: SelfEnrollmentStatus[]
    client_id: string[]
  }

  sort: Sort
  pagination: Pagination
}

let sortKeys = [
  'name',
  'birth_date',
  'ssn',
  'client',
  'status'
] as const

type SortKey = typeof sortKeys[number]
type SortDirection = 'asc' | 'desc'

interface Sort {
  field: SortKey
  direction: SortDirection
}

interface Pagination {
  page: number
  pageSize: number
}

let initialState: State = {
  searchParams: {
    query: '',
    filters: {
      status: [],
      client_id: []
    },
    sort: {
      field: 'name',
      direction: 'asc'
    },
    pagination: {
      page: 0,
      pageSize: 10
    }
  }
}

function reducer (state: State, action: Action): State {
  switch (action.type) {
    case 'query/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/merge': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: {
            ...state.searchParams.filters,
            ...action.payload
          },
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/clear': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: initialState.searchParams.query,
          filters: initialState.searchParams.filters,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'sort/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          sort: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'pagination/page/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            page: action.payload
          }
        }
      }
    }

    case 'pagination/page-size/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            pageSize: action.payload,
            page: 0
          }
        }
      }
    }

    default: {
      return state
    }
  }
}

let sortMapper: Record<string, string> = {
  name: 'last_name'
}

let filterMapper: Record<string, string> = {
  client: 'client_id'
}

function isDefaultSearchParams (searchParams: Pick<SearchParams, 'query' | 'filters'>): boolean {
  return (
    searchParams.query === initialState.searchParams.query &&
    searchParams.filters.status.length === initialState.searchParams.filters.status.length &&
    searchParams.filters.status.every(v => initialState.searchParams.filters.status.includes(v)) &&
    searchParams.filters.client_id.length === initialState.searchParams.filters.client_id.length &&
    searchParams.filters.client_id.every(v => initialState.searchParams.filters.client_id.includes(v))
  )
}

export function SelfEnrollmentPage (): JSX.Element {
  let [ searchParams, setSearchParams ] = useSearchParams()

  let [ state, dispatch ] = useReducer(reducer, initialState, state => ({
    ...state,
    searchParams: {
      ...state.searchParams,
      query: (searchParams.get('search')?.trim() ?? '') || initialState.searchParams.query
    }
  }))

  let [ debouncedSearchQuery ] = useDebounceValue(state.searchParams.query, 500)

  let query = useQuery<unknown, unknown, QueryData, QueryKey>({
    queryKey: [
      'self-enrollment',
      { ...state.searchParams, query: debouncedSearchQuery }
    ],

    async queryFn ({ queryKey }) {
      let [ , searchParams ] = queryKey
      let { query, filters, sort, pagination } = searchParams
      let normalizedQuery = query.trim().toLowerCase()

      let sp = new URLSearchParams([
        [ 'count', pagination.pageSize.toString() ],
        [ 'page', pagination.page.toString() ],
        [ 'on', sortMapper[sort.field] ?? sort.field ],
        [ 'dir', sort.direction ],
        ...filters.status.map(v => [ 'filters.status', v ]),
        ...filters.client_id.map(v => [ 'filters.client_id', v ]),
        ...(normalizedQuery.length > 0 ? [[ 'search', normalizedQuery ]] : [])
      ])

      let response = await http.getRequest(`/admin/index?${sp.toString()}`)

      return {
        ...response,
        data: response.data.map((entry: Entry) => ({
          ...entry,
          id: entry.hash
        }))
      }
    },

    placeholderData: keepPreviousData
  })

  let clientsQuery = useQuery<unknown, unknown, { data: Client[] }>({
    queryKey: [
      'clients'
    ],

    async queryFn () {
      let response = await http.getRequest('/admin/clients?count=9999')
      return response.data
    },

    placeholderData: keepPreviousData
  })

  let columns = useMemo<Array<Column<Entry>>>(() => [
    {
      name: 'name',
      header: 'Name',
      isSortable: true,
      getValue (entry) {
        return [
          entry.first_name,
          entry.last_name
        ]
          .filter(Boolean)
          .map(v => (v as string).trim())
          .join(' ')
      }
    },
    {
      name: 'birth_date',
      header: 'Birthday',
      isSortable: true,
      getValue (entry) {
        if (typeof entry.birth_date !== 'string') {
          return null
        }

        return Table.formatDate(entry.birth_date)
      }
    },
    {
      name: 'ssn',
      header: 'SSN',
      isSortable: true
    },
    {
      name: 'client',
      header: 'Client',
      isSortable: true
    },
    {
      name: 'status',
      header: 'Status',
      isSortable: true,
      getValue (entry) {
        if (typeof entry.status !== 'string') {
          return null
        }

        return selfEnrollmentStatusMapper[entry.status] ?? entry.status
      }
    }
  ], [])

  let filters = useMemo(() => {
    return [
      {
        name: 'status',
        value: state.searchParams.filters.status,
        options: [
          { value: SelfEnrollmentStatus.Started, label: selfEnrollmentStatusMapper[SelfEnrollmentStatus.Started] },
          { value: SelfEnrollmentStatus.Finished, label: selfEnrollmentStatusMapper[SelfEnrollmentStatus.Finished] },
          { value: SelfEnrollmentStatus.None, label: selfEnrollmentStatusMapper[SelfEnrollmentStatus.None] }
        ]
      },
      ...(Array.isArray(clientsQuery.data) && clientsQuery.data.length > 0
        ? [
          {
            name: 'client',
            value: state.searchParams.filters.client_id,
            options: clientsQuery.data.map(client => ({
              value: client.key,
              label: client.display
            }))
          }
        ]
        : []
      )
    ]
  }, [ state.searchParams.filters, clientsQuery.data ])

  let handleFilterChange = useCallback(<T extends keyof Entry>(name: T, value: Array<Entry[T]>): void => {
    let mappedName = filterMapper[name as string] ?? name

    dispatch({
      type: 'filters/merge',
      payload: {
        [mappedName]: value
      }
    })
  }, [])

  let handleFiltersClear = useCallback((): void => {
    dispatch({
      type: 'filters/clear'
    })

    setSearchParams(params => {
      params.delete('search')
      return params
    })
  }, [ setSearchParams ])

  let handleSortChange = useCallback((field: keyof Entry, direction: SortDirection): void => {
    function isSortKey (key: unknown): key is SortKey {
      return (sortKeys as readonly unknown[]).includes(key)
    }

    if (!isSortKey(field)) {
      return
    }

    dispatch({
      type: 'sort/set',
      payload: {
        field,
        direction
      }
    })
  }, [])

  let handlePageChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page/set',
      payload: value - 1
    })
  }, [])

  let handlePageSizeChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page-size/set',
      payload: value
    })
  }, [])

  let pagination = useMemo(() => {
    return {
      ...state.searchParams.pagination,
      totalFiltered: query.data?.total ?? 0,
      totalUnfiltered: query.data?.total ?? 0
    }
  }, [ state.searchParams.pagination, query.data ])

  return (
    <div>
      <PageHeader className={styles.pageHeader}>
        <h1>
          <span>Self Enrollment</span>
        </h1>

        <SearchInput
          value={state.searchParams.query}
          placeholder='Search by name, birthday, SSN, client and status'
          onChange={event => {
            dispatch({
              type: 'query/set',
              payload: event.target.value
            })

            setSearchParams(params => {
              let value = event.target.value.trim()

              if (value) {
                params.set('search', value)
              }

              if (!value) {
                params.delete('search')
              }

              return params
            })
          }}
        />
      </PageHeader>

      <Table.Table
        data={query.data?.data ?? []}
        queryStatus={query.status}
        noDataPlaceholder='No self enrollments found'
        forceClearFilters={!isDefaultSearchParams(state.searchParams)}
        columns={columns}
        filters={filters}
        sort={state.searchParams.sort}
        pagination={pagination}
        onFilterChange={handleFilterChange}
        onFiltersClear={handleFiltersClear}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  )
}
